import { motion } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";
import { useLang } from "@/lib/i18n";

export function Journey() {
  const { t, lang } = useLang();
  const JOURNEY = [
    { place: lang === "pt" ? "São Tomé e Príncipe" : "São Tomé and Príncipe", desc: t("journey.1.d") },
    { place: lang === "pt" ? "Alemanha" : "Germany", desc: t("journey.2.d") },
    { place: t("journey.3.t"), desc: t("journey.3.d") },
  ];
  return (
    <section id="journey" data-testid="journey-section" className="py-28 lg:py-40 bg-surface/40 border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Chapter index="11" label={t("journey.chapter")} testid="journey-chapter" />
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
              <span className="italic text-gold-light">{t("journey.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              data-testid="journey-profile-btn"
              href="#resources"
              className="inline-flex items-center mt-10 px-8 py-4 border border-white/20 text-ivory font-mono text-[11px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t("journey.cta")}
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {JOURNEY.map((j, i) => (
            <motion.div
              key={j.place}
              data-testid={`journey-step-${i}`}
              className="relative pl-10 pb-12 last:pb-0 border-l border-white/10"
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
            >
              <span className={`absolute -left-[7px] top-1 w-[13px] h-[13px] rounded-full border-2 ${i === JOURNEY.length - 1 ? "border-gold bg-gold shadow-[0_0_14px_rgba(212,175,55,0.5)]" : "border-white/30 bg-ink"}`} />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold">{j.place}</span>
              <p className="mt-3 text-white/60 leading-relaxed text-base md:text-lg">{j.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Partnership() {
  const { t, list } = useLang();
  const stages = list("partnership.stages");
  return (
    <section data-testid="partnership-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="12" label={t("partnership.chapter")} testid="partnership-chapter" />
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-6">
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
              {t("partnership.h2a")} <span className="italic text-gold-light">{t("partnership.h2b")}</span>
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed text-base md:text-lg max-w-xl">{t("partnership.p")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {list("partnership.cats").map((c) => (
                <span key={c} data-testid={`partner-cat-${c.toLowerCase().replace(/\s+/g, "-")}`} className="border border-white/10 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="lg:col-span-5 lg:col-start-8 flex flex-col items-center">
            <Reveal className="w-full">
              <div className="border border-gold/50 bg-gold/[0.05] px-8 py-4 text-center">
                <span className="font-serif text-xl text-ivory">BSGOTECH</span>
              </div>
            </Reveal>
            <span className="w-px h-8 bg-gold/40" />
            <div className="grid grid-cols-2 gap-3 w-full">
              {list("partnership.flow").map((p, i) => (
                <motion.div
                  key={p}
                  className="border border-white/10 px-4 py-3 text-center bg-ink"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                >
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/60">{p}</span>
                </motion.div>
              ))}
            </div>
            <div className="flex flex-col items-center w-full mt-0">
              {stages.map((s, i) => (
                <motion.div key={s} className="flex flex-col items-center w-full"
                  initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 + i * 0.15 }}>
                  <span className="w-px h-8 bg-gold/40" />
                  <div className={`px-10 py-3 text-center border ${i === stages.length - 1 ? "border-emerald-500/50 bg-forest/10" : "border-white/15 bg-surface"}`}>
                    <span className={`font-mono text-[11px] tracking-[0.3em] uppercase ${i === stages.length - 1 ? "text-emerald-400" : "text-ivory"}`}>{s}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
